import { gql, useLazyQuery } from "@apollo/client";
import { useCallback } from "react";

export type DesignerFileCardApi = {
  id: string;
  fileId: string;
  fileType: "ProjectProfile" | "BeforeAndAfter" | "DesignBoard";
  designerId?: string | null;
  published?: boolean | null;
  title?: string | null;
  image?: {
    _id?: string | null;
    url?: string | null;
    path?: string | null;
    title?: string | null;
    description?: string | null;
    credit?: string | null;
  } | null;
  projectCategory?: string | null;
  projectStyle?: string | null;
  style?: string | null;
  specialRequirements?: (string | null)[] | null;
  budgetRange?: string | null;
  squareFootage?: string | null;
  displayOrder?: number | null;
  designBoardType?: string | null;
  roomType?: (string | null)[] | null;
  pattern?: (string | null)[] | null;
  texture?: (string | null)[] | null;
  color?: (string | null)[] | null;
  finish?: (string | null)[] | null;
};

type GetDesignerFileCardsVars = {
  limit: number;
  offset?: number | null;
  designerId?: string | null;
};

type ProjectFileCardsData = {
  getProjectFileCards: DesignerFileCardApi[];
};

type BeforeAfterFileCardsData = {
  getBeforeAfterFileCards: DesignerFileCardApi[];
};

type DesignBoardFileCardsData = {
  getDesignBoardFileCards: DesignerFileCardApi[];
};

const GET_PROJECT_FILE_CARDS = gql`
  query GetProjectFileCards($limit: Int!, $offset: Int, $designerId: ID) {
    getProjectFileCards(limit: $limit, offset: $offset, designerId: $designerId) {
      id
      fileId
      fileType
      designerId
      published
      title
      projectCategory
      projectStyle
      style
      specialRequirements
      budgetRange
      squareFootage
      displayOrder
      designBoardType
      roomType
      pattern
      texture
      color
      finish
      image {
        _id
        url
        path
        title
        description
        credit
      }
    }
  }
`;

const GET_BEFORE_AFTER_FILE_CARDS = gql`
  query GetBeforeAfterFileCards($limit: Int!, $offset: Int, $designerId: ID) {
    getBeforeAfterFileCards(
      limit: $limit
      offset: $offset
      designerId: $designerId
    ) {
      id
      fileId
      fileType
      designerId
      published
      title
      projectCategory
      projectStyle
      style
      specialRequirements
      budgetRange
      squareFootage
      displayOrder
      designBoardType
      roomType
      pattern
      texture
      color
      finish
      image {
        _id
        url
        path
        title
        description
        credit
      }
    }
  }
`;

const GET_DESIGN_BOARD_FILE_CARDS = gql`
  query GetDesignBoardFileCards($limit: Int!, $offset: Int, $designerId: ID) {
    getDesignBoardFileCards(
      limit: $limit
      offset: $offset
      designerId: $designerId
    ) {
      id
      fileId
      fileType
      designerId
      published
      title
      projectCategory
      projectStyle
      style
      specialRequirements
      budgetRange
      squareFootage
      displayOrder
      designBoardType
      roomType
      pattern
      texture
      color
      finish
      image {
        _id
        url
        path
        title
        description
        credit
      }
    }
  }
`;

export const useGetDesignerFileCards = () => {
  const [getProjectFileCardsQuery] = useLazyQuery<
    ProjectFileCardsData,
    GetDesignerFileCardsVars
  >(GET_PROJECT_FILE_CARDS);
  const [getBeforeAfterFileCardsQuery] = useLazyQuery<
    BeforeAfterFileCardsData,
    GetDesignerFileCardsVars
  >(GET_BEFORE_AFTER_FILE_CARDS);
  const [getDesignBoardFileCardsQuery] = useLazyQuery<
    DesignBoardFileCardsData,
    GetDesignerFileCardsVars
  >(GET_DESIGN_BOARD_FILE_CARDS);

  const getProjectFileCards = useCallback(
    async (offset: number, limit: number, designerId?: string | null) => {
      const result = await getProjectFileCardsQuery({
        variables: { offset, limit, designerId },
      });
      return result.data?.getProjectFileCards ?? [];
    },
    [getProjectFileCardsQuery]
  );

  const getBeforeAfterFileCards = useCallback(
    async (offset: number, limit: number, designerId?: string | null) => {
      const result = await getBeforeAfterFileCardsQuery({
        variables: { offset, limit, designerId },
      });
      return result.data?.getBeforeAfterFileCards ?? [];
    },
    [getBeforeAfterFileCardsQuery]
  );

  const getDesignBoardFileCards = useCallback(
    async (offset: number, limit: number, designerId?: string | null) => {
      const result = await getDesignBoardFileCardsQuery({
        variables: { offset, limit, designerId },
      });
      return result.data?.getDesignBoardFileCards ?? [];
    },
    [getDesignBoardFileCardsQuery]
  );

  return {
    getProjectFileCards,
    getBeforeAfterFileCards,
    getDesignBoardFileCards,
  };
};
